import React, { useState } from "react";
import { Box, Flex, Textarea, Button, Collapse, Icon } from "@chakra-ui/react";
import { MdCode, MdFileUpload } from "react-icons/md";
import toast from "react-hot-toast";

/**
 * Parses JSON input and returns an array of sections.
 * Accepts: full content object, single section, or array of sections.
 * @param {string} jsonStr - Raw JSON string
 * @returns {Array} Parsed sections array
 */
function parseSectionsFromJson(jsonStr) {
    const parsed = JSON.parse(jsonStr);
    if (parsed.sections && Array.isArray(parsed.sections)) return parsed.sections;
    if (Array.isArray(parsed)) return parsed;
    if (parsed.number && parsed.title !== undefined) return [parsed];
    throw new Error("Invalid format: expected {sections:[...]}, [{...}], or a single section object");
}

/**
 * Attempts to parse and import JSON sections, showing toast feedback.
 * @param {string} jsonText - Raw JSON string to parse
 * @param {function} onImport - Callback with parsed sections array
 * @param {function} setJsonText - State setter to clear the text field
 * @param {function} setOpen - State setter to close the panel
 */
function executeJsonImport(jsonText, onImport, setJsonText, setOpen) {
    try {
        const sections = parseSectionsFromJson(jsonText);
        onImport(sections);
        setJsonText("");
        setOpen(false);
        toast.success(`${sections.length} section(s) imported`);
    } catch (err) {
        toast.error(`JSON Error: ${err.message}`);
    }
}

/**
 * Renders the JSON import textarea and import button inside the collapse panel.
 * @param {object} props
 * @param {string} props.jsonText - Current JSON text value
 * @param {function} props.setJsonText - Setter for JSON text
 * @param {function} props.onImport - Callback with parsed sections array
 * @param {function} props.setOpen - Setter to close the panel after import
 * @returns {React.ReactElement}
 */
function JsonImportForm({ jsonText, setJsonText, onImport, setOpen }) {
    return (
        <Box p="16px" borderRadius="12px" border="1px dashed" borderColor="whiteAlpha.300">
            <Textarea rows={6} fontFamily="mono" fontSize="0.78rem" value={jsonText}
                onChange={(e) => setJsonText(e.target.value)}
                placeholder='Paste JSON: {"sections": [...]}, [{...}], or a single section {...}' />
            <Flex justify="flex-end" mt="8px">
                <Button size="sm" colorScheme="blue" isDisabled={!jsonText.trim()}
                    leftIcon={<Icon as={MdFileUpload} />}
                    onClick={() => executeJsonImport(jsonText, onImport, setJsonText, setOpen)}>
                    Import
                </Button>
            </Flex>
        </Box>
    );
}

/**
 * JSON import panel with toggle button and collapsible textarea form.
 * @param {object} props
 * @param {function} props.onImport - Callback with parsed sections array
 * @returns {React.ReactElement}
 */
function JsonImportPanel({ onImport }) {
    const [jsonText, setJsonText] = useState("");
    const [open, setOpen] = useState(false);

    return (
        <Box mb="16px">
            <Button size="sm" variant="ghost" mb={open ? "8px" : "0"} leftIcon={<Icon as={MdCode} />}
                onClick={() => setOpen(!open)}>
                {open ? "Hide JSON import" : "Import from JSON"}
            </Button>
            <Collapse in={open}>
                <JsonImportForm jsonText={jsonText} setJsonText={setJsonText} onImport={onImport} setOpen={setOpen} />
            </Collapse>
        </Box>
    );
}

export default JsonImportPanel;
